/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/organisms/AppSidebar.tsx
 * Release: v2.0.0 - Sidebar contestuale per gruppi/ruoli
 * Note: Visibilità basata su group_id e role, superadmin vede tutto
 */

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthProvider';
import { APP_VERSION } from '../../version';
import { usePendingCounts } from '../../contexts/PendingCountsContext';
import { DevSettingsPanel } from '../admin/DevSettingsPanel';

interface NavItem {
  label: string;
  path?: string; // Opzionale se ha children
  icon: string;
  groups?: string[]; // group_id ammessi
  roles?: string[]; // ruoli ammessi
  adminOnly?: boolean; // Solo per admin/superadmin
  superAdminOnly?: boolean; // Solo per superadmin
  allowedForPendingProfile?: boolean; // Permesso anche se profilo incompleto
  isAction?: boolean; // Se true, non naviga ma esegue un'azione
  onClick?: () => void; // Callback per azioni speciali
  children?: NavItem[]; // Submenu nested
  badgeId?: 'pending-total' | 'pending-apparati' | 'pending-clienti'; // Badge dinamico
}

interface AppSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * ============================================================
 * STRUTTURA MENU RAZIONALIZZATA - v5.9.61
 * ============================================================
 *
 * Golden Path operativo:
 * 1. INGRESSO → apparato arriva (temporaneo = fase, non qualità)
 * 2. CONFERMA → admin valida i dati (apparato diventa definitivo)
 * 3. LAVORAZIONE → analisi guasti, riparazione
 * 4. COMMERCIALE → preventivi, fatture, documenti
 *
 * Terminology:
 * - "in attesa" = da confermare (fase temporanea)
 * - "in accettazione" = confermati, pronti per lavorazione
 * - Evitare "temporaneo/definitivo" come aggettivi di qualità
 * ============================================================
 */

const NAV_ITEMS: NavItem[] = [
  // ============================================================
  // DASHBOARD - Sempre visibile
  // ============================================================
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: '📊',
    allowedForPendingProfile: true
    // Contenuto parametrizzato in base a utente/gruppo
  },

  // ============================================================
  // INGRESSO E ACCETTAZIONE - Fase 1 del workflow
  // ============================================================
  // Gestisce: arrivo apparati, conferma dati, inserimento nuovi
  {
    label: 'Ingresso e Accettazione',
    icon: '📥',
    roles: ['superadmin', 'admin'],
    groups: ['accettazione', 'magazzino', 'officina', 'contabilita', 'client', 'clienti'],
    children: [
      // --- Dashboard V2 (nuova) ---
      {
        label: 'Dashboard V2',
        path: '/admin/accettazione-v2',
        icon: '🎯',
        roles: ['superadmin', 'admin'],
        groups: ['accettazione', 'magazzino', 'officina', 'contabilita']
        // Nuova dashboard unificata accettazione
      },
      // --- Visualizzazione (tutti) ---
      {
        label: 'In Attesa',
        path: '/admin/pending',
        icon: '⏳',
        roles: ['superadmin', 'admin'],
        groups: ['accettazione', 'magazzino', 'officina', 'contabilita'],
        badgeId: 'pending-total' // Badge con conteggio totale pending (turbine + clienti)
        // Da confermare - inseriti da mobile o da "Nuovo ingresso rapido"
      },
      {
        label: 'Apparati in Accettazione',
        path: '/accettazione',
        icon: '✅',
        roles: ['superadmin', 'admin'],
        groups: ['accettazione', 'magazzino', 'officina', 'contabilita', 'client', 'clienti']
        // Confermati - pronti per lavorazione
      },
      // --- Azioni di inserimento (adminOnly) ---
      {
        label: 'Nuovo Ingresso Rapido',
        path: '/admin/nuovo-inserimento',
        icon: '⚡',
        adminOnly: true
        // Crea apparato in stato "in attesa" - da confermare dopo
      },
      {
        label: 'Nuovo Ingresso Completo',
        icon: '📝',
        adminOnly: true,
        // Inserimento diretto come definitivo con tutti i dati
        children: [
          {
            label: 'Da Biglietto Cliente',
            path: '/accettazione/digitalizza-biglietto',
            icon: '💳'
            // OCR biglietto + dati apparato completi
          },
          {
            label: 'Inserimento Manuale',
            path: '/accettazione/compila-dati-essenziali',
            icon: '✏️'
            // Form completo cliente + apparato
          }
        ]
      }
    ]
  },

  // ============================================================
  // LAVORAZIONE - Fase 2 del workflow
  // ============================================================
  // Gestisce: analisi guasti, riparazione, stato lavorazione
  {
    label: 'Lavorazione',
    icon: '🔧',
    roles: ['superadmin', 'admin'],
    groups: ['accettazione', 'magazzino', 'officina', 'contabilita'],
    children: [
      {
        label: 'Analisi Guasti',
        path: '/analisi-guasti-preventivi',
        icon: '🔍',
        roles: ['superadmin', 'admin'],
        groups: ['accettazione', 'magazzino', 'officina', 'contabilita']
        // Diagnosi e foto componenti danneggiati
      },
      // Legacy - accessibile solo a superadmin per riferimenti storici
      {
        label: 'Analisi Guasti (Legacy)',
        path: '/analisi-guasti-preventivi-old',
        icon: '🕰️',
        superAdminOnly: true
        // Versione precedente per comparazioni
      }
      // NOTE: "Apparati in Lavorazione" (/lavorazione) - DA IMPLEMENTARE
      // NOTE: Sezione "Commerciale" (preventivi, storico, documenti) - DA IMPLEMENTARE
    ]
  },
  
  // === GESTIONE CLIENTI (admin/superadmin e gruppi interni) ===
  {
    label: 'Gestione Clienti',
    icon: '👥',
    // Solo ruoli, senza groups - superadmin/admin vedono sempre
    roles: ['superadmin', 'admin'],
    children: [
      {
        label: 'Lista Clienti',
        path: '/admin/users',
        icon: '📋',
        roles: ['superadmin', 'admin']
      },
      {
        label: 'Clienti in Attesa',
        path: '/admin/pending-customers',
        icon: '⏳',
        roles: ['superadmin', 'admin']
      },
      {
        label: 'Nuovo Cliente',
        icon: '➕',
        roles: ['superadmin', 'admin'],
        children: [
          {
            label: 'Da Biglietto',
            path: '/admin/new-customer/business-card',
            icon: '📸'
            // Eredita permessi dal parent
          },
          {
            label: 'Inserimento Manuale',
            path: '/admin/profile-form',
            icon: '✍️'
            // Eredita permessi dal parent
          }
        ]
      }
      // NOTE: "Archiviati" (/admin/archived-customers) - DA IMPLEMENTARE
      // NOTE: "Impostazioni" (/admin/customer-settings) - DA IMPLEMENTARE
    ]
  },

  // Gestione utenti interni e gruppi (superadmin, admin)
  {
    label: 'Amministrazione',
    path: '/admin',
    icon: '🔧',
    roles: ['superadmin', 'admin']
    // Gestione utenti interni, gruppi, permessi
  },

  // Archivio dati iniettori (banca dati): consultazione + CRUD con approvazione (admin/superadmin).
  // Solo 'roles' (no groups) → visibile ad admin e superadmin, non ai gruppi operativi.
  {
    label: 'Archivio dati iniettori',
    path: '/admin/apparati/banca-dati',
    icon: '💉',
    roles: ['superadmin', 'admin']
  },

  // === IMPOSTAZIONI SVILUPPO (superAdminOnly) ===
  // Apre modale con toggle mock/live data, latenza simulata, logging
  {
    label: 'Impostazioni Dev',
    icon: '⚗️',
    superAdminOnly: true,
    isAction: true
    // onClick viene aggiunto dinamicamente nel componente
  },

  // Il MIO profilo (tutti gli utenti) - sempre accessibile
  {
    label: 'Il Mio Profilo',
    path: '/profile',
    icon: '👤',
    allowedForPendingProfile: true
    // Nessun controllo di ruolo - sempre visibile per utenti autenticati
  },

  // === CONFIGURAZIONE SISTEMA (superAdminOnly) ===
  {
    label: 'Configuration Hub',
    path: '/admin/config-hub',
    icon: '⚙️',
    superAdminOnly: true
    // Setup: AI Providers, Database, Google Maps, API esterne
  },

  // === TEST E SVILUPPO (superAdminOnly) - Menu nested ===
  {
    label: 'Test',
    icon: '🧪',
    superAdminOnly: true,
    children: [
      {
        label: 'AI',
        icon: '🤖',
        superAdminOnly: true,
        children: [
          {
            label: 'Config rel.1',
            path: '/admin/openai-config',
            icon: '🤖',
            superAdminOnly: true
          },
          {
            label: 'Config rel.2',
            path: '/admin/config-hub',
            icon: '🤖',
            superAdminOnly: true
          }
        ]
      },
      {
        label: 'Prove varie',
        icon: '✨',
        superAdminOnly: true,
        children: [
          {
            label: 'Controlli Debug',
            path: '/test-controlli',
            icon: '🐛',
            superAdminOnly: true
          },
          {
            label: 'Professional Components',
            path: '/test-professional',
            icon: '✨',
            superAdminOnly: true
          },
          {
            label: 'Remote Camera',
            path: '/test-remote-camera',
            icon: '📱',
            superAdminOnly: true
          },
          {
            label: 'OCR Bigliettini',
            path: '/business-card-ocr',
            icon: '🎴',
            superAdminOnly: true
          },
          // NOTE: Test Core Imports (/test-core) - Route commentata in App.tsx
          {
            label: 'Photo Crop',
            path: '/test-universal-photo-crop',
            icon: '📸',
            roles: ['superadmin', 'admin']
          }
        ]
      },
      {
        label: 'Dialogicom UI',
        icon: '🎨',
        superAdminOnly: true,
        children: [
          {
            label: 'Rel.1',
            path: '/ui-dialogicom',
            icon: '🎨',
            superAdminOnly: true
          },
          {
            label: 'Rel.2',
            path: '/ui-dialogicom-rel_2',
            icon: '🎨',
            superAdminOnly: true
          }
        ]
      },
      {
        label: 'Apps Modulari',
        icon: '🚀',
        superAdminOnly: true,
        children: [
          {
            label: 'Menu Apps',
            path: '/menu-hub',
            icon: '🚀',
            superAdminOnly: true
          },
          {
            label: 'App Lavorazione',
            path: '/apps/lavorazione',
            icon: '🔧',
            superAdminOnly: true
          }
        ]
      }
    ]
  },
];

export const AppSidebar: React.FC<AppSidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pendingCounts = usePendingCounts();
  const group = user?.group_id || 'guest';
  const role = user?.role || 'guest';
  const isAdmin = role === 'admin' || role === 'superadmin';
  const isSuperAdmin = role === 'superadmin';
  const needsProfileCompletion = user?.needs_profile_completion || false;

  // State per pannello DevSettings (toggle mock/live data)
  const [showDevSettings, setShowDevSettings] = useState(false);

  // Helper per ottenere il valore del badge
  const getBadgeValue = (badgeId: NavItem['badgeId']): number => {
    if (!badgeId) return 0;
    switch (badgeId) {
      case 'pending-total':
        return pendingCounts.total;
      case 'pending-apparati':
        return pendingCounts.apparati;
      case 'pending-clienti':
        return pendingCounts.clienti;
      default:
        return 0;
    }
  };


  // Funzione per determinare se un path è attivo
  const isActiveRoute = (itemPath: string) => {
    const currentPath = location.pathname;

    // Match esatto per la maggior parte dei path
    if (currentPath === itemPath) {
      return true;
    }

    // === GESTIONE PROFILO ===
    if (itemPath === '/profile') {
      // /profile attivo anche su /admin/profile-form (redirect admin)
      return currentPath === '/profile' || currentPath === '/admin/profile-form';
    }

    // === GESTIONE AMMINISTRAZIONE ===
    if (itemPath === '/admin') {
      // Solo pagina base e gruppi (non altre sezioni admin)
      return currentPath === '/admin' ||
             currentPath === '/admin/' ||
             currentPath === '/admin/groups';
    }

    // === GESTIONE CLIENTI ===
    if (itemPath === '/admin/users') {
      return currentPath === '/admin/users';
    }
    if (itemPath === '/admin/pending-customers') {
      return currentPath === '/admin/pending-customers';
    }
    if (itemPath === '/admin/profile-form') {
      return currentPath === '/admin/profile-form' ||
             currentPath.startsWith('/admin/profile-form/');
    }
    if (itemPath === '/admin/new-customer/business-card') {
      return currentPath === '/admin/new-customer/business-card';
    }
    if (itemPath === '/admin/archived-customers') {
      return currentPath === '/admin/archived-customers';
    }
    if (itemPath === '/admin/customer-settings') {
      return currentPath === '/admin/customer-settings';
    }

    // === INGRESSO E ACCETTAZIONE ===
    if (itemPath === '/admin/pending') {
      return currentPath === '/admin/pending' || currentPath === '/admin/pending-turbines';
    }
    if (itemPath === '/admin/nuovo-inserimento') {
      return currentPath === '/admin/nuovo-inserimento';
    }
    if (itemPath === '/accettazione') {
      // Match esatto per evitare conflitti con sottopercorsi
      return currentPath === '/accettazione';
    }
    if (itemPath === '/accettazione/digitalizza-biglietto') {
      return currentPath === '/accettazione/digitalizza-biglietto';
    }
    if (itemPath === '/accettazione/compila-dati-essenziali') {
      return currentPath === '/accettazione/compila-dati-essenziali';
    }

    // === LAVORAZIONE ===
    if (itemPath === '/analisi-guasti-preventivi') {
      return currentPath === '/analisi-guasti-preventivi' ||
             currentPath.startsWith('/analisi-guasti-preventivi/');
    }
    if (itemPath === '/analisi-guasti-preventivi-old') {
      return currentPath === '/analisi-guasti-preventivi-old';
    }

    return false;
  };

  // Filtra voci menu in base a gruppo e ruolo
  const allowed = NAV_ITEMS.filter(item => {
    // Se l'utente deve completare il profilo, mostra solo le voci consentite
    if (needsProfileCompletion && !item.allowedForPendingProfile) {
      return false;
    }

    // Link al profilo sempre visibile per tutti gli utenti autenticati
    
    // Controllo gruppi specifici
    const groupOk = !item.groups || item.groups.includes(group);
    
    // Controllo ruoli
    const roleOk = !item.roles || item.roles.includes(role);
    
    // Se ci sono sia groups che roles, basta che una delle due condizioni sia soddisfatta (OR)
    let groupOrRoleOk = true;
    if (item.groups && item.roles) {
      groupOrRoleOk = groupOk || roleOk;
    } else {
      groupOrRoleOk = groupOk && roleOk;
    }
    
    // Controllo admin only
    const adminOk = !item.adminOnly || isAdmin;
    
    // Controllo superadmin only
    const superAdminOk = !item.superAdminOnly || isSuperAdmin;
    
    return groupOrRoleOk && adminOk && superAdminOk;
  });

  // Tutti gli utenti vedono una singola Dashboard unificata
  // Il contenuto è parametrizzato internamente in base a gruppo/ruolo
  const menuItems = allowed;

  // State per gestire apertura/chiusura submenu
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  const toggleSubmenu = (label: string) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(label)) {
        newSet.delete(label);
      } else {
        newSet.add(label);
      }
      return newSet;
    });
  };

  // Funzione helper per verificare se un item è visibile per l'utente corrente
  const isItemAllowed = (item: NavItem): boolean => {
    // Se l'utente deve completare il profilo, mostra solo le voci consentite
    if (needsProfileCompletion && !item.allowedForPendingProfile) {
      return false;
    }

    // Controllo gruppi specifici
    const groupOk = !item.groups || item.groups.includes(group);

    // Controllo ruoli
    const roleOk = !item.roles || item.roles.includes(role);

    // Se ci sono sia groups che roles, basta che una delle due condizioni sia soddisfatta (OR)
    let groupOrRoleOk = true;
    if (item.groups && item.roles) {
      groupOrRoleOk = groupOk || roleOk;
    } else {
      groupOrRoleOk = groupOk && roleOk;
    }

    // Controllo admin only
    const adminOk = !item.adminOnly || isAdmin;

    // Controllo superadmin only
    const superAdminOk = !item.superAdminOnly || isSuperAdmin;

    return groupOrRoleOk && adminOk && superAdminOk;
  };

  // Component ricorsivo per renderizzare menu items con submenu
  const MenuItemRenderer: React.FC<{ item: NavItem; level?: number }> = ({ item, level = 0 }) => {
    // Personalizza l'etichetta della Dashboard
    let displayLabel = item.label;
    let displayIcon = item.icon;

    if (item.path === '/dashboard') {
      if (isSuperAdmin) {
        displayLabel = 'Dashboard SuperAdmin';
        displayIcon = '👑';
      } else if (isAdmin) {
        displayLabel = 'Dashboard Admin';
        displayIcon = '⚙️';
      } else {
        // Personalizza per gruppo
        switch (group) {
          case 'accettazione':
            displayLabel = 'Dashboard Accettazione';
            displayIcon = '🚗';
            break;
          case 'officina':
            displayLabel = 'Dashboard Officina';
            displayIcon = '🔧';
            break;
          case 'magazzino':
            displayLabel = 'Dashboard Magazzino';
            displayIcon = '📦';
            break;
          case 'ufficio':
            displayLabel = 'Dashboard Ufficio';
            displayIcon = '💼';
            break;
          case 'client':
            displayLabel = 'Dashboard Cliente';
            displayIcon = '👥';
            break;
          default:
            displayLabel = 'Dashboard';
            displayIcon = '📊';
        }
      }
    }

    const isExpanded = expandedMenus.has(item.label);
    // Filtra children visibili per l'utente corrente
    const visibleChildren = item.children?.filter(isItemAllowed) || [];
    const hasChildren = visibleChildren.length > 0;
    const paddingLeft = level * 8; // 8px indent per livello

    // Se ha children, è un menu collapsabile
    if (hasChildren) {
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleSubmenu(item.label)}
            className={`flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 w-full text-left text-slate-300 hover:text-white hover:bg-slate-700/50`}
            style={{ paddingLeft: `${16 + paddingLeft}px` }}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{displayIcon}</span>
              <span className={displayLabel.length > 15 ? "leading-tight text-left" : "text-left"}>
                {displayLabel}
              </span>
            </div>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Submenu collapsabile - usa children già filtrati */}
          {isExpanded && (
            <div className="ml-2 mt-1 space-y-1 border-l-2 border-slate-700">
              {visibleChildren.map(child => (
                <MenuItemRenderer key={child.label} item={child} level={level + 1} />
              ))}
            </div>
          )}
        </div>
      );
    }

    // Gestione azioni speciali
    if (item.isAction) {
      return (
        <button
          key={item.label}
          onClick={() => {
            if (item.path === '#config-cards') {
              window.dispatchEvent(new CustomEvent('openConfigCardLayout'));
              onClose();
            } else if (item.label === 'Impostazioni Dev') {
              // Apre pannello DevSettings per toggle mock/live data
              setShowDevSettings(true);
              onClose();
            } else {
              onClose();
            }
          }}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 w-full text-left text-slate-300 hover:text-white hover:bg-slate-700/50"
          style={{ paddingLeft: `${16 + paddingLeft}px` }}
        >
          <span className="text-lg">{displayIcon}</span>
          <span className={displayLabel.length > 15 ? "leading-tight text-left" : "text-left"}>
            {displayLabel}
          </span>
        </button>
      );
    }

    // Link normale
    const isCurrentlyActive = item.path ? isActiveRoute(item.path) : false;
    const badgeValue = getBadgeValue(item.badgeId);

    return (
      <button
        key={item.label}
        onClick={() => {
          if (item.path) {
            navigate(item.path);
            onClose();
          }
        }}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 w-full text-left ${
          isCurrentlyActive
            ? 'bg-green-600/20 text-green-400 border border-green-500/30 shadow-lg'
            : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
        }`}
        style={{ paddingLeft: `${16 + paddingLeft}px` }}
      >
        <span className="text-lg">{displayIcon}</span>
        <span className={displayLabel.length > 15 ? "leading-tight text-left flex-1" : "text-left flex-1"}>
          {displayLabel}
        </span>
        {/* Badge per pending counts */}
        {badgeValue > 0 && (
          <span className="ml-auto px-2 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white animate-pulse">
            {badgeValue}
          </span>
        )}
      </button>
    );
  };

  return (
    <aside className={`w-48 shrink-0 bg-gradient-to-b from-slate-900 to-slate-800 border-r border-slate-700 fixed left-0 top-0 h-screen p-2 z-50 transition-transform duration-300 flex flex-col ${
      isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
    }`}>
      {/* Header con logo e pulsante chiudi (mobile) */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="text-center flex-1">
            <div className="text-2xl font-bold text-green-400 mb-1">CoronaTek</div>
            <div className="text-xs text-slate-400">Turbomanager</div>
          </div>
          {/* Pulsante chiudi per mobile */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-700/50 transition-colors text-slate-400"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <nav className="space-y-2 flex-1 overflow-y-auto">
        {menuItems.map((item) => (
          <MenuItemRenderer key={item.label} item={item} />
        ))}
      </nav>

      {/* Footer sidebar con versione e logo Dialogicom - fisso in basso */}
      <div className="flex-shrink-0 pt-4 pb-2 border-t border-slate-700/50 mt-4">
        <div className="flex flex-col items-center gap-3">
          {/* Versione app */}
          <div className="text-center">
            <div className="text-[10px] text-slate-500 mb-0.5">
              v{APP_VERSION.VERSION}
            </div>
            <div className="text-[9px] text-slate-600">
              SW {APP_VERSION.SERVICE_WORKER}
            </div>
          </div>

          {/* Logo Dialogicom */}
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-slate-400">Developed by</span>
            <img
              src="/assets/Dialogicom_Logo.png"
              alt="Dialogicom"
              className="h-10 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
              title="Developed by Dialogicom"
            />
          </div>
        </div>
      </div>

      {/* DevSettings Panel - toggle mock/live data */}
      <DevSettingsPanel
        isOpen={showDevSettings}
        onClose={() => setShowDevSettings(false)}
      />
    </aside>
  );
};

export default AppSidebar;


